import { useState } from 'react'
import { SearchScreen } from './search-screen'
import {
  searchArticlesQuery,
  searchBrandsQuery,
  searchOffersQuery,
  useSearchArticlesQuery,
  useSearchBrandsQuery,
  useSearchOffersQuery,
} from '../../queries'
import { createParam } from 'solito'

const { useParam } = createParam()

const filters = ['', 'articles', 'brands', 'offers'] as const
export type SearchFilter = (typeof filters)[number]

export const SearchScreenContainer = () => {
  const [query] = useParam('query')
  const [searchTerm, setSearchTerm] = useState(query || '')
  const [filter, setFilter] = useState<SearchFilter>('')

  const {
    data: dataBrands,
    fetchMore: fetchMoreBrands,
    networkStatus: networkStatusBrands,
    previousData: previousDataBrands,
  } = useSearchBrandsQuery({
    variables: {
      ...searchBrandsQuery.defaultVariables,
      searchTerm,
    },
    notifyOnNetworkStatusChange: true,
  })

  const {
    data: dataOffers,
    fetchMore: fetchMoreOffers,
    networkStatus: networkStatusOffers,
    previousData: previousDataOffers,
  } = useSearchOffersQuery({
    variables: {
      ...searchOffersQuery.defaultVariables,
      searchTerm,
    },
    notifyOnNetworkStatusChange: true,
  })

  const {
    data: dataArticles,
    fetchMore: fetchMoreArticles,
    networkStatus: networkStatusArticles,
    previousData: previousDataArticles,
  } = useSearchArticlesQuery({
    variables: {
      ...searchArticlesQuery.defaultVariables,
      searchTerm,
    },
    notifyOnNetworkStatusChange: true,
  })

  const handleSearch = (newSearchTerm) => {
    setSearchTerm(newSearchTerm)
  }

  const handleFilter = (newFilter) => {
    setFilter(newFilter)
  }

  const totalBrandDocs = dataBrands?.searchBrands?.totalDocs || 0
  const hasMoreBrands = totalBrandDocs > (dataBrands?.searchBrands?.docs.length || 0)

  const handleFetchMoreBrands = async () => {
    if (!hasMoreBrands || !dataBrands) return
    await fetchMoreBrands({
      variables: {
        page: dataBrands.searchBrands.page + 1,
        limit: 10,
      },
    })
  }

  const totalOfferDocs = dataOffers?.searchOffers?.totalDocs || 0
  const hasMoreOffers = totalOfferDocs > (dataOffers?.searchOffers?.docs.length || 0)

  const handleFetchMoreOffers = async () => {
    if (!hasMoreOffers || !dataOffers) return
    await fetchMoreOffers({
      variables: {
        page: dataOffers.searchOffers.page + 1,
        limit: 10,
      },
    })
  }

  const totalArticleDocs = dataArticles?.searchArticles?.totalDocs || 0
  const hasMoreArticles = totalArticleDocs > (dataArticles?.searchArticles?.docs.length || 0)

  const handleFetchMoreArticles = async () => {
    if (!hasMoreArticles || !dataArticles) return
    await fetchMoreArticles({
      variables: {
        page: dataArticles.searchArticles.page + 1,
        limit: 10,
      },
    })
  }

  // Reuse the previous data while the new data is fetching to avoid flickering
  const brands = dataBrands?.searchBrands?.docs || previousDataBrands?.searchBrands?.docs || []
  const offers = dataOffers?.searchOffers?.docs || previousDataOffers?.searchOffers?.docs || []
  const articles =
    dataArticles?.searchArticles?.docs || previousDataArticles?.searchArticles?.docs || []

  return (
    <SearchScreen
      initialQuery={searchTerm || ''}
      filter={filter}
      articles={articles}
      articlesHasMore={hasMoreArticles}
      articlesFetchMore={handleFetchMoreArticles}
      articlesFetchingMore={networkStatusArticles === 3}
      brands={brands}
      brandsHasMore={hasMoreBrands}
      brandsFetchMore={handleFetchMoreBrands}
      brandsFetchingMore={networkStatusBrands === 3}
      offers={offers}
      offersHasMore={hasMoreOffers}
      offersFetchMore={handleFetchMoreOffers}
      offersFetchingMore={networkStatusOffers === 3}
      onSearch={handleSearch}
      onFilterChange={handleFilter}
      fetching={
        networkStatusArticles === 2 || networkStatusBrands === 2 || networkStatusOffers === 2
      }
      loading={
        networkStatusArticles === 1 || networkStatusBrands === 1 || networkStatusOffers === 1
      }
    />
  )
}
